import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/lib/seo";
import { IMAGES, PROBLEMS, REVENUE, OUTCOMES } from "@/content/site";
import { Reveal, SectionHeader, StagePill, CtaLink, IconTile } from "@/components/site/primitives";
import { JourneySteps } from "@/components/site/JourneySteps";
import { ServicesGrid } from "@/components/site/ServicesGrid";
import { CtaBand } from "@/components/site/CtaBand";

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden bg-white">
    <div className="dot-grid absolute inset-y-0 left-0 w-full opacity-50 lg:w-1/2" aria-hidden="true" />
    <div className="container-x relative grid items-center gap-12 pb-20 pt-12 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-16">
      <Reveal className="lg:col-span-6">
        <StagePill>Idea stage · Proposed pilot in Bihar</StagePill>
        <h1 className="mt-8 text-[2.75rem] font-semibold leading-[1.02] sm:text-6xl lg:text-[4.5rem]" data-testid="home-hero-title">
          Healthcare, <span className="text-teal-700">Closer</span> to&nbsp;Home<span className="text-magenta">.</span>
        </h1>
        <p className="mt-7 max-w-xl font-display text-xl leading-snug text-plum/90 md:text-2xl">
          Connecting underserved communities with accessible, affordable, and technology-enabled primary healthcare.
        </p>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate md:text-[17px]">
          SwastSaathi is developing a community-based healthcare network for rural India. Through local access points and
          simple digital tools, we aim to connect patients with qualified healthcare professionals, diagnostics and
          medicines — strengthening the providers that already exist, rather than replacing them.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <CtaLink to="/partners#enquiry" testId="hero-partner-cta">Partner With Us</CtaLink>
          <CtaLink to="/vision-impact" testId="hero-vision-cta" variant="secondary">Explore Our Vision</CtaLink>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="relative lg:col-span-6">
        <figure className="relative">
          <div className="absolute -right-6 -top-6 hidden h-40 w-40 rounded-full bg-teal-50 lg:block" aria-hidden="true" />
          <picture>
            <source srcSet="/hero-proposed-model.webp?v=2" type="image/webp" />
            <img
              src="/hero-proposed-model.jpg?v=2"
              alt="Illustrative photo of the proposed model: a community health worker helps a rural mother and child join a video consultation with a doctor at a local access point, connected to a general physician, specialists, diagnostics, pharmacy and follow-up care"
              width="1264"
              height="848"
              data-testid="home-hero-image"
              className="relative aspect-[3/2] w-full rounded-[2rem] object-cover shadow-[0_40px_80px_-40px_rgba(44,15,101,0.55)]"
              fetchpriority="high"
            />
          </picture>
          <figcaption className="mt-4 text-xs text-slate">Illustrative image of the proposed community-based model. SwastSaathi does not yet operate centres.</figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

const Problem = () => (
  <section data-testid="home-problem" className="bg-mint py-20 lg:py-28">
    <div className="container-x grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <SectionHeader eyebrow="The problem we aim to solve" title="For many rural families, primary care is still a journey away." intro="These are common, well-recognised barriers to primary healthcare in underserved communities — the challenges SwastSaathi is designed to address." />
          <Reveal delay={0.1} className="mt-10 hidden lg:block">
            <img src={IMAGES.elder} alt="An elderly man in a white turban in rural India" loading="lazy" className="aspect-[4/3] w-full rounded-[1.5rem] object-cover" />
          </Reveal>
        </div>
      </div>
      <ol className="space-y-4 lg:col-span-7">
        {PROBLEMS.map((p, i) => (
          <Reveal as="li" key={p.title} delay={i * 0.06}>
            <article data-testid={`problem-item-${i}`} className="card-soft card-soft-hover flex gap-6 p-6 sm:p-8">
              <span className="font-display text-sm font-semibold text-magenta">0{i + 1}</span>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p.icon className="h-5 w-5 text-teal-700" strokeWidth={1.8} aria-hidden="true" />
                  <h3 className="text-xl font-semibold">{p.title}</h3>
                </div>
                <p className="mt-3 leading-relaxed text-slate">{p.body}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const Solution = () => (
  <section data-testid="home-solution" className="bg-white py-20 lg:py-28">
    <div className="container-x grid items-center gap-14 lg:grid-cols-12">
      <Reveal className="order-2 lg:order-1 lg:col-span-6">
        <figure>
          <picture>
            <source srcSet="/solution-local-access.webp" type="image/webp" />
            <img
              src="/solution-local-access.jpg"
              alt="Illustration of a doctor examining a rural patient with a stethoscope at a local SwastSaathi access point, with the message One local point of contact for coordinated, connected care"
              width="1489"
              height="1056"
              loading="lazy"
              data-testid="home-solution-image"
              className="w-full rounded-[1.5rem] object-cover shadow-[0_30px_60px_-30px_rgba(44,15,101,0.45)]"
            />
          </picture>
          <figcaption className="mt-3 text-xs text-slate">Illustrative image of the proposed model. SwastSaathi does not yet operate centres.</figcaption>
        </figure>
      </Reveal>
      <div className="order-1 lg:order-2 lg:col-span-6 lg:pl-6">
        <SectionHeader eyebrow="Our proposed solution" title="A Connected Healthcare Platform" intro="SwastSaathi proposes a technology-enabled network that connects underserved communities with qualified healthcare professionals and trusted service providers through local access points." />
        <Reveal delay={0.1}>
          <p className="mt-5 text-base leading-relaxed text-slate md:text-lg" data-testid="solution-platform-text">
            Our proposed platform brings together patient access, clinical consultations, diagnostic services, medicine access, referrals and follow-up in one coordinated journey—while clinical decisions remain with licensed professionals.
          </p>
          <div className="mt-8 rounded-2xl border-l-4 border-teal bg-mint p-6" data-testid="solution-vision-callout">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">Our vision</p>
            <p className="mt-2 font-display text-lg font-medium leading-snug text-plum md:text-xl">
              Build a scalable, sustainable healthcare network that strengthens existing providers and makes primary healthcare easier to access, closer to home.
            </p>
          </div>
          <Link to="/healthcare-model" data-testid="solution-model-link" className="group mt-9 inline-flex items-center gap-2 font-semibold text-teal-700">
            See the proposed healthcare model <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Journey = () => (
  <section id="journey" data-testid="home-journey" className="grain relative overflow-hidden bg-plum py-20 lg:py-28">
    <div className="container-x relative">
      <Reveal className="max-w-3xl">
        <p className="eyebrow text-teal-100">How SwastSaathi works</p>
        <h2 className="mt-4 text-3xl font-semibold leading-[1.1] text-white sm:text-4xl lg:text-[2.75rem]">The proposed patient journey</h2>
        <p className="mt-5 text-base leading-relaxed text-white/70 md:text-lg">A simple, five-step path we intend to test and refine during the Bihar pilot.</p>
      </Reveal>
      <div className="mt-16"><JourneySteps /></div>
    </div>
  </section>
);

const Services = () => (
  <section data-testid="home-services" className="bg-white py-20 lg:py-28">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <SectionHeader eyebrow="Proposed service categories" title="What a SwastSaathi centre is designed to coordinate." />
        <Reveal><p className="max-w-sm text-sm leading-relaxed text-slate">None of these services are operational yet. Each will be delivered by or with licensed partners, subject to validation and applicable regulations.</p></Reveal>
      </div>
      <div className="mt-14"><ServicesGrid /></div>
    </div>
  </section>
);

const BusinessModel = () => (
  <section data-testid="home-business-model" className="bg-mint py-20 lg:py-28">
    <div className="container-x grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeader eyebrow="Our business model" title="Partnership-led and asset-light." intro="Rather than building hospitals or employing every clinician, SwastSaathi intends to coordinate care through partners — keeping costs low and focusing capital on community access points and technology." />
        <Reveal delay={0.1} className="mt-8">
          <CtaLink to="/investors" testId="business-model-investors-cta" variant="secondary">For investors & incubators</CtaLink>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-plum/70">Potential revenue sources</p>
        </Reveal>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {REVENUE.map((r, i) => (
            <Reveal key={r.title} delay={(i % 2) * 0.06}>
              <div data-testid={`revenue-item-${i}`} className="card-soft h-full p-6">
                <h3 className="text-lg font-semibold leading-snug">{r.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal><p className="mt-6 text-sm text-slate">All revenue streams are potential and remain subject to applicable regulations, partner agreements and validation during the pilot.</p></Reveal>
      </div>
    </div>
  </section>
);

const Impact = () => (
  <section data-testid="home-impact" className="bg-white py-20 lg:py-28">
    <div className="container-x">
      <SectionHeader eyebrow="Our impact vision" title="The outcomes we intend to work towards." intro="These are intended outcomes — not proven results. We will measure them honestly as the model is piloted." />
      <ul className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {OUTCOMES.map((o, i) => (
          <Reveal as="li" key={o.title} delay={i * 0.06}>
            <div data-testid={`outcome-item-${i}`} className="card-soft card-soft-hover flex h-full flex-col gap-6 p-6">
              <IconTile icon={o.icon} tone={i % 2 ? "plum" : "teal"} />
              <p className="font-display text-lg font-semibold leading-snug text-plum">{o.title}</p>
            </div>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export default function HomePage() {
  usePageMeta({
    title: "SwastSaathi — Community Primary Healthcare for Rural India",
    description: "SwastSaathi is an idea-stage health-tech startup building a community-based primary healthcare network for rural India, starting with a proposed pilot in Bihar.",
    path: "/",
  });
  return (
    <>
      <Hero />
      <Problem />
      <Solution />
      <Journey />
      <Services />
      <BusinessModel />
      <Impact />
      <CtaBand testId="home-closing-cta" />
    </>
  );
}
